import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, Controller, useFieldArray, useFormState } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'
import Select from 'react-select'
import _ from 'lodash'
import { v4 } from 'uuid'
import classNames from 'classnames'
import { toast } from 'react-hot-toast'

import { Prompt } from 'Src/history'
import ToggleSwitch from 'Src/components/Toggle'
import { fetchFlows } from 'Actions/bots'
import { getAllBots, getBotFlows, getSpecificCampaign } from 'Selectors/bots'
import backArrow from 'Assets/images/back-arrow.svg?url'
import deleteIcon from '../../assets/images/x-circle.svg?url'
import api from 'Src/api'
import {
  page_options,
  visitor_options,
  behavior_options,
  page_condition_options,
  visitor_condition_options,
  behavior_condition_options,
  action_options
} from './options'

const UPDATE_BOT_URL = 'v1/bots/:botId'

const Campaign = () => {
  const params = useParams()
  const campaignId = params.id
  const [botId, setBotId] = useState('')
  const [botActive, setBotActive] = useState(false)
  const bots = useSelector(getAllBots)
  const flows = useSelector((state) => getBotFlows(state, botId))
  const campaign = useSelector((state) => getSpecificCampaign(state, campaignId))
  const formMethods = useForm({
    defaultValues: {
      flow: null,
      behavior_conditions: [{ property: '', predicate: '', value: '' }]
    }
  })
  const { register, control, handleSubmit, watch, reset, resetField } = formMethods
  const { isDirty, isSubmitted, errors } = useFormState({ control })
  const watchBotValue = watch('bot')
  const dispatch = useDispatch()

  const {
    fields: messageFields,
    append: messageAppend,
    remove: messageRemove
  } = useFieldArray({
    control,
    name: 'override_messages',
    shouldUnregister: true
  })

  const {
    fields: qrFields,
    append: qrAppend,
    remove: qrRemove
  } = useFieldArray({
    control,
    name: 'override_qrs',
    shouldUnregister: true
  })

  const {
    fields: pageFields,
    append: pageAppend,
    remove: pageRemove
  } = useFieldArray({
    control,
    name: 'page_conditions',
    shouldUnregister: true
  })

  const {
    fields: visitorFields,
    append: visitorAppend,
    remove: visitorRemove
  } = useFieldArray({
    control,
    name: 'visitor_conditions',
    shouldUnregister: true
  })

  const { fields: behaviorFields } = useFieldArray({
    control,
    name: 'behavior_conditions',
    shouldUnregister: true
  })

  const handleRemove = useCallback(
    (action) => {
      const { type, index } = action
      switch (type) {
        case 'message':
          messageRemove(index)
          break

        case 'qr':
          qrRemove(index)
          break

        case 'page':
          pageRemove(index)
          break

        case 'visitor':
          visitorRemove(index)
          break
      }
    },
    [messageRemove, qrRemove, pageRemove, visitorRemove]
  )

  useEffect(() => {
    if (botId) {
      dispatch(fetchFlows(botId))
    }
  }, [dispatch, botId])

  useEffect(() => {
    if (watchBotValue) {
      setBotId(watchBotValue.value)
      resetField('flow')
    }
  }, [watchBotValue, resetField])

  useEffect(() => {
    const getDropdownValue = (options, value) => {
      return _.find(options, { value })
    }

    if (Object.keys(campaign).length) {
      reset({
        name: campaign.name,
        bot: { label: campaign.botName, value: campaign.botId },
        flow: { label: campaign.flowName, value: campaign.flowId },
        override_messages:
          campaign.overrideMessages.map((message) => {
            return { message }
          }) ?? [],
        override_qrs:
          campaign.overrideQuickReplies.map((qr) => {
            return { label: qr.label, action: getDropdownValue(action_options, qr.action), value: qr.value }
          }) ?? [],
        page_conditions:
          campaign.page_conditions.match_all.map((condition) => {
            return {
              property: getDropdownValue(page_options, condition.property),
              predicate: getDropdownValue(page_condition_options, condition.predicate),
              value: condition.value
            }
          }) ?? [],
        visitor_conditions:
          campaign.visitor_conditions.match_all.map((condition) => {
            return {
              property: getDropdownValue(visitor_options, condition.property),
              predicate: getDropdownValue(visitor_condition_options, condition.predicate),
              value: condition.value
            }
          }) ?? [],
        behavior_conditions:
          campaign.behavior_conditions.match_all.map((condition) => {
            return {
              property: getDropdownValue(behavior_options, condition.property),
              predicate: getDropdownValue(behavior_condition_options, condition.predicate),
              value: condition.value
            }
          }) ?? [],
        showPopup: campaign.showPopup
      })
      setBotActive(campaign.active)
    }
  }, [campaign, reset])

  const flowSelectOptions = useMemo(() => {
    return flows
      .slice()
      .reverse()
      .map((flow) => {
        return {
          value: flow.id,
          label: flow.settings?.name || 'Default'
        }
      })
  }, [flows])

  const getCampaignRules = (bots) => {
    const botsWithCampaigns = bots.filter((bot) => bot.settings.campaign_rules)
    const campaign_rules = botsWithCampaigns
      .map((bot) => {
        return bot.settings.campaign_rules
      })
      .flat()
    return campaign_rules
  }

  const removeCampaignFromBot = async (botId, ruleId) => {
    const bot = _.find(bots, { id: botId })
    const campaign_rules = bot.settings.campaign_rules.filter((rule) => rule.id !== ruleId)
    const payload = {
      widget_type: bot.widget_type,
      status: bot.status,
      settings: {
        campaign_rules
      }
    }
    try {
      await api.patch(`v1/bots/${botId}`, payload, { urlParams: { botId } })
    } catch (err) {
      console.error('Error removing campaign rule from bot')
    }
  }

  const addRuleToBotSettings = async (rule) => {
    const bot = _.find(bots, { id: rule.botId })
    let campaign_rules = getCampaignRules(bots)
    let hasBotChanged = false
    let originalBotId = null
    let existingRuleId = null

    const existingRuleIndex = campaign_rules?.findIndex((campaign_rule) => campaign_rule.id === rule.id)
    const isExistingRule = existingRuleIndex !== undefined && existingRuleIndex !== -1
    if (isExistingRule) {
      const existingRule = campaign_rules[existingRuleIndex]
      hasBotChanged = existingRule.botId !== bot.id
      if (hasBotChanged) {
        existingRuleId = existingRule.id
        originalBotId = existingRule.botId
      }
      campaign_rules[existingRuleIndex] = rule
      campaign_rules = campaign_rules.filter((rule) => rule.botId === bot.id)
    } else {
      campaign_rules = campaign_rules.filter((rule) => rule.botId === bot.id)
      if (campaign_rules?.length) {
        campaign_rules = [...campaign_rules, { ...rule }]
      } else {
        campaign_rules = [{ ...rule }]
      }
    }
    const payload = {
      widget_type: bot.widget_type,
      status: bot.status,
      settings: {
        campaign_rules
      }
    }
    try {
      await api.patch(UPDATE_BOT_URL, payload, { urlParams: { botId: rule.botId } })
      if (hasBotChanged) {
        removeCampaignFromBot(originalBotId, existingRuleId)
      }
      toast.success('Saved campaign rule')
    } catch (err) {
      toast.error('Error while saving rule')
    }
  }

  const onSubmit = (formData) => {
    const rule = {
      id: v4(),
      name: '',
      botId: '',
      botName: '',
      active: botActive,
      flowId: '',
      flowName: '',
      overrideMessages: [],
      overrideQuickReplies: [],
      page_conditions: {
        match_all: []
      },
      visitor_conditions: {
        match_all: []
      },
      behavior_conditions: {
        match_all: []
      },
      showPopup: false
    }
    if (campaignId) {
      rule.id = campaignId
    }
    rule.name = formData.name
    rule.botId = formData.bot?.value
    rule.botName = formData.bot?.label
    rule.flowId = formData.flow?.value
    rule.flowName = formData.flow?.label
    rule.showPopup = formData.showPopup
    if (formData.override_messages.length) {
      rule.overrideMessages = formData.override_messages.map((message) => message.message)
    }
    if (formData.override_qrs.length) {
      rule.overrideQuickReplies = formData.override_qrs.map((qr) => {
        return { label: qr.label, action: qr.action?.value, value: qr.value }
      })
    }
    rule.page_conditions.match_all = formData.page_conditions.map((condition) => {
      return { property: condition.property.value, predicate: condition.predicate.value, value: condition.value }
    })
    rule.visitor_conditions.match_all = formData.visitor_conditions.map((condition) => {
      return { property: condition.property.value, predicate: condition.predicate.value }
    })
    rule.behavior_conditions.match_all = formData.behavior_conditions.map((condition) => {
      return { property: condition.property.value, predicate: condition.predicate.value, value: condition.value }
    })

    addRuleToBotSettings(rule)
  }

  const bot_options = bots.map((bot) => {
    return { label: bot.name, value: bot.id }
  })

  return (
    <div className='page-container campaigns'>
      <div className='main-content'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='page-header'>
            <Link className='back-button mr-3' to='/campaigns'>
              <img src={backArrow} />
            </Link>

            <div className='section-heading'>
              <div className='section-name'>CAMPAIGN</div>
              <div className='section-title'>
                <input
                  placeholder='Give this campaign a name'
                  {...register('name', { required: 'Please enter a campaign name' })}
                />
                {errors.name && <small className='ml-2 text-danger'>{errors.name.message}</small>}
              </div>
            </div>
          </div>
          <div>
            <div className='campaign-step'>
              <div className='campaign-step-index'>
                <span>1</span>
              </div>
              <div className='campaign-step-content'>
                <div className='mb-2'>
                  <strong>Which bot is this campaign for?</strong>
                </div>
                <div>Select a bot</div>
                <div className='settings-block'>
                  <Controller
                    name='bot'
                    rules={{ required: 'Please select a bot' }}
                    control={control}
                    render={({ field }) => <Select placeholder='Select a bot' {...field} options={bot_options} />}
                  />
                  {errors.bot && <small className='ml-2 text-danger'>{errors.bot.message}</small>}
                </div>
              </div>
            </div>

            <div className='campaign-step'>
              <div className='campaign-step-index'>
                <span>2</span>
              </div>
              <div className='campaign-step-content'>
                <div className='mb-2'>
                  <strong>Which flow do you want to show?</strong>
                </div>
                <div>Select a flow</div>
                <div className='settings-block mb-4'>
                  <Controller
                    name='flow'
                    rules={{ required: 'Please select a flow' }}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder='Select a flow'
                        className={classNames({ disabled: !watchBotValue })}
                        options={flowSelectOptions}
                      />
                    )}
                  />
                  {errors.flow && <small className='ml-2 text-danger'>{errors.flow.message}</small>}
                </div>

                <div>Override starting messages</div>
                {messageFields.map((field, index) => {
                  return (
                    <div key={field.id}>
                      <div className='d-flex align-items-center'>
                        <textarea
                          {...register(`override_messages.${index}.message`, { required: 'Please enter a message' })}
                          className='text-input mr-2 mt-2'
                        />
                        <div onClick={() => handleRemove({ type: 'message', index })}>
                          <img src={deleteIcon} />
                        </div>
                      </div>
                      {errors.override_messages && errors.override_messages[index] && (
                        <small className='ml-2 text-danger'>{errors.override_messages[index].message.message}</small>
                      )}
                    </div>
                  )
                })}
                <div className='add-label pl-0' onClick={() => messageAppend({ message: '' })}>
                  + Add Message
                </div>

                <div>Override starting quick replies</div>
                {qrFields.map((field, index) => {
                  return (
                    <div className='page-settings-row d-flex mt-2 align-items-center' key={field.id}>
                      <div style={{ width: '40%', marginRight: '15px' }}>
                        <input
                          {...register(`override_qrs.${index}.label`, { required: 'Enter a label' })}
                          className='text-input'
                          placeholder='Name of the option shown to visitor'
                        />
                        {errors.override_qrs && errors.override_qrs[index] && (
                          <small className='ml-2 text-danger'>{errors.override_qrs[index].label?.message}</small>
                        )}
                      </div>
                      <div className='settings-block'>
                        <Controller
                          name={`override_qrs.${index}.action`}
                          control={control}
                          rules={{ required: 'Select an action' }}
                          render={({ field }) => <Select {...field} options={action_options} />}
                        />
                        {errors.override_qrs && errors.override_qrs[index] && (
                          <small className='ml-2 text-danger'>{errors.override_qrs[index].action?.message}</small>
                        )}
                      </div>
                      <div className='settings-block'>
                        <input
                          {...register(`override_qrs.${index}.value`, { required: 'Enter a value' })}
                          className='text-input'
                          placeholder='Value'
                        />
                        {errors.override_qrs && errors.override_qrs[index] && (
                          <small className='ml-2 text-danger'>{errors.override_qrs[index].value?.message}</small>
                        )}
                      </div>
                      <div onClick={() => handleRemove({ type: 'qr', index })}>
                        <img src={deleteIcon} />
                      </div>
                    </div>
                  )
                })}
                <div className='add-label pl-0' onClick={() => qrAppend({ label: '', action: '', value: '' })}>
                  + Add Quick Reply
                </div>
              </div>
            </div>

            <div className='campaign-step'>
              <div className='campaign-step-index'>
                <span>3</span>
              </div>
              <div className='campaign-step-content'>
                <strong>Which pages do you want this campaign to be active?</strong>
                {pageFields.map((field, index) => {
                  return (
                    <div className='page-settings-row d-flex mt-2 align-items-center' key={field.id}>
                      <div className='settings-block'>
                        <Controller
                          name={`page_conditions.${index}.property`}
                          control={control}
                          rules={{ required: 'Select a property' }}
                          render={({ field }) => <Select {...field} options={page_options} />}
                        />
                        {errors.page_conditions && errors.page_conditions[index] && (
                          <small className='ml-2 text-danger'>{errors.page_conditions[index].property?.message}</small>
                        )}
                      </div>
                      <div className='settings-block'>
                        <Controller
                          name={`page_conditions.${index}.predicate`}
                          control={control}
                          rules={{ required: 'Select a condition' }}
                          render={({ field }) => <Select {...field} options={page_condition_options} />}
                        />
                        {errors.page_conditions && errors.page_conditions[index] && (
                          <small className='ml-2 text-danger'>{errors.page_conditions[index].predicate?.message}</small>
                        )}
                      </div>
                      <div style={{ width: '40%', marginRight: '15px' }}>
                        <input
                          {...register(`page_conditions.${index}.value`, { required: 'Enter a value' })}
                          className='text-input'
                          placeholder='value'
                        />
                        {errors.page_conditions && errors.page_conditions[index] && (
                          <small className='ml-2 text-danger'>{errors.page_conditions[index].value?.message}</small>
                        )}
                      </div>
                      <div onClick={() => handleRemove({ type: 'page', index })}>
                        <img src={deleteIcon} />
                      </div>
                    </div>
                  )
                })}
                <div className='add-label pl-0' onClick={() => pageAppend({ property: '', predicate: '', value: '' })}>
                  + Add Condition
                </div>
              </div>
            </div>
            <div className='campaign-step'>
              <div className='campaign-step-index'>
                <span>4</span>
              </div>
              <div className='campaign-step-content'>
                <strong>Who are you targeting on this page?</strong>
                {visitorFields.map((field, index) => {
                  return (
                    <div className='page-settings-row d-flex mt-2 align-items-center' key={field.id}>
                      <div className='settings-block'>
                        <Controller
                          name={`visitor_conditions.${index}.property`}
                          rules={{ required: 'Select a property' }}
                          control={control}
                          render={({ field }) => <Select {...field} options={visitor_options} />}
                        />
                        {errors.visitor_conditions && errors.visitor_conditions[index] && (
                          <small className='ml-2 text-danger'>
                            {errors.visitor_conditions[index].property?.message}
                          </small>
                        )}
                      </div>
                      <div className='settings-block'>
                        <Controller
                          name={`visitor_conditions.${index}.predicate`}
                          control={control}
                          rules={{ required: 'Select a condition' }}
                          render={({ field }) => <Select {...field} options={visitor_condition_options} />}
                        />
                        {errors.visitor_conditions && errors.visitor_conditions[index] && (
                          <small className='ml-2 text-danger'>
                            {errors.visitor_conditions[index].predicate?.message}
                          </small>
                        )}
                      </div>
                      <div onClick={() => handleRemove({ type: 'visitor', index })}>
                        <img src={deleteIcon} />
                      </div>
                    </div>
                  )
                })}
                <div
                  className='add-label pl-0'
                  onClick={() => visitorAppend({ property: '', predicate: '', value: '' })}
                >
                  + Add Condition
                </div>
              </div>
            </div>

            <div className='campaign-step'>
              <div className='campaign-step-index'>
                <span>5</span>
              </div>
              <div className='campaign-step-content'>
                <strong>When should the message pop up?</strong>
                {behaviorFields.map((field, index) => {
                  return (
                    <div className='page-settings-row d-flex mt-2' key={field.id}>
                      <div className='settings-block'>
                        <Controller
                          name={`behavior_conditions.${index}.property`}
                          control={control}
                          rules={{ required: 'Select a behavior' }}
                          render={({ field }) => <Select {...field} options={behavior_options} />}
                        />
                        {errors.behavior_conditions && errors.behavior_conditions[index] && (
                          <small className='ml-2 text-danger'>
                            {errors.behavior_conditions[index].property?.message}
                          </small>
                        )}
                      </div>
                      <div className='settings-block'>
                        <Controller
                          name={`behavior_conditions.${index}.predicate`}
                          control={control}
                          rules={{ required: 'Select a condition' }}
                          render={({ field }) => <Select {...field} options={behavior_condition_options} />}
                        />
                        {errors.behavior_conditions && errors.behavior_conditions[index] && (
                          <small className='ml-2 text-danger'>
                            {errors.behavior_conditions[index].predicate?.message}
                          </small>
                        )}
                      </div>
                      <div style={{ width: '40%', marginRight: '15px' }}>
                        <input
                          {...register(`behavior_conditions.${index}.value`, {
                            required: 'Enter a value',
                            pattern: {
                              value: /^\d+$/g,
                              message: 'Please enter a number'
                            }
                          })}
                          style={{
                            width: '100%',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '5px 10px',
                            minHeight: '38px'
                          }}
                          placeholder='Value (in seconds)'
                        />
                        {errors.behavior_conditions && errors.behavior_conditions[index] && (
                          <small className='ml-2 text-danger'>{errors.behavior_conditions[index].value?.message}</small>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className='campaign-step'>
              <div className='campaign-step-index'>
                <span>6</span>
              </div>
              <div className='campaign-step-content'>
                <strong>How would you like to present the campaign?</strong>
                <div className='settings-row clearfix'>
                  <div className='settings-label'>Pop up the starting message</div>
                  <div className='settings-switch'>
                    <Controller
                      name='showPopup'
                      control={control}
                      render={({ field: { value, onChange } }) => {
                        return <ToggleSwitch checked={value} onClick={() => onChange(!value)} />
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='campaign-step'>
              <div />
              <button type='submit' className='button-primary'>
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
      <div className='campaigns-sidebar-container'>
        <div className='settings-heading'>Manage</div>
        <div className='settings-row clearfix'>
          <div className='settings-label'>Active</div>
          <div className='settings-switch'>
            <ToggleSwitch checked={botActive} onClick={() => setBotActive(!botActive)} />
          </div>
        </div>
      </div>
      <Prompt when={isDirty && !isSubmitted} message='Are you sure you want to discard your unsaved changes?' />
    </div>
  )
}

export default Campaign
